import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router";

import { allFeatureRoles, RouteConstants } from "../../../models/enums";
import { useCurrentUserService } from "../../../services/ServiceHooks";
import { useStore } from "../../../stores/StoreContext";
import { LoadingPage } from "../../Pages"; // or your existing loading UI

interface IAIFeaturePermissionsGuardProps {
  children: ReactNode;
}

export const AIFeaturePermissionsGuard: FunctionComponent<IAIFeaturePermissionsGuardProps> = props => {
  const { permissionStore } = useStore();
  const currentUserService = useCurrentUserService();
  const [checking, setChecking] = useState(true);
  const [hasPermissions, setHasPermissions] = useState<boolean | null>(null);

  const hasAnyAIFeaturePermissions = (): boolean => {
    return permissionStore.hasAnyRole(allFeatureRoles);
  };

  useEffect(() => {
    let mounted = true;

    const ensureUserPermissions = async () => {
      try {
        await currentUserService.ensureUserPermissions();
      } catch (err) {
        console.error("Failed to load permissions", err);
      } finally {
        if (mounted) {
          setHasPermissions(hasAnyAIFeaturePermissions());

          setChecking(false);
        }
      }
    };

    // If permission store already initialized, skip calling ensureUserPermissions
    if (hasAnyAIFeaturePermissions()) {
      setHasPermissions(true);
      setChecking(false);
    } else {
      ensureUserPermissions();
    }

    return () => {
      mounted = false;
    };
  }, [permissionStore, currentUserService]);

  if (checking) {
    return <LoadingPage loadingInformation="Checking permissions..." />;
  }

  if (!hasPermissions) {
    return <Navigate to={`/${RouteConstants.NoAIFeaturePermissions}`} replace />;
  }

  return props.children;
};
